import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, TrendingUp, TrendingDown, MessageSquare, X, ChevronRight, ThumbsUp, ThumbsDown, Minus, MapPin, Calendar, Filter, Store } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { KPICard } from '../components/ui/KPICard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Treemap } from 'recharts';
// Region filter options
const regions = [{
  id: 'konzern',
  name: 'Gesamtkonzern',
  icon: Building2
}, {
  id: 'nord',
  name: 'EDEKA Nord'
}, {
  id: 'minden-hannover',
  name: 'EDEKA Minden-Hannover'
}, {
  id: 'rhein-ruhr',
  name: 'EDEKA Rhein-Ruhr'
}, {
  id: 'hessenring',
  name: 'EDEKA Hessenring'
}, {
  id: 'nordbayern',
  name: 'EDEKA Nordbayern-Sachsen-Thüringen'
}, {
  id: 'suedwest',
  name: 'EDEKA Südwest'
}, {
  id: 'suedbayern',
  name: 'EDEKA Südbayern'
}];
// Mock data per region
const regionData: Record<string, {
  positive: number;
  neutral: number;
  negative: number;
  total: number;
  avgRating: number;
  trend: number;
}> = {
  konzern: {
    positive: 12450,
    neutral: 4230,
    negative: 1820,
    total: 18500,
    avgRating: 4.2,
    trend: 0.15
  },
  nord: {
    positive: 1820,
    neutral: 580,
    negative: 210,
    total: 2610,
    avgRating: 4.4,
    trend: 0.22
  },
  'minden-hannover': {
    positive: 2100,
    neutral: 720,
    negative: 380,
    total: 3200,
    avgRating: 4.1,
    trend: 0.08
  },
  'rhein-ruhr': {
    positive: 2450,
    neutral: 890,
    negative: 460,
    total: 3800,
    avgRating: 4.0,
    trend: -0.05
  },
  hessenring: {
    positive: 1650,
    neutral: 520,
    negative: 180,
    total: 2350,
    avgRating: 4.3,
    trend: 0.18
  },
  nordbayern: {
    positive: 1580,
    neutral: 490,
    negative: 220,
    total: 2290,
    avgRating: 4.2,
    trend: 0.12
  },
  suedwest: {
    positive: 1720,
    neutral: 580,
    negative: 240,
    total: 2540,
    avgRating: 4.3,
    trend: 0.2
  },
  suedbayern: {
    positive: 1130,
    neutral: 450,
    negative: 130,
    total: 1710,
    avgRating: 4.5,
    trend: 0.25
  }
};
// Topic clusters data
const topicClusters: Record<string, Array<{
  name: string;
  size: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  comments: Array<{
    text: string;
    market: string;
    date: string;
    rating: number;
  }>;
}>> = {
  konzern: [{
    name: 'Frische & Qualität',
    size: 4200,
    sentiment: 'positive',
    comments: [{
      text: 'Die Obst- und Gemüseabteilung ist immer top frisch!',
      market: 'EDEKA Center Hamburg',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Beste Fleischtheke in der Umgebung, sehr gute Beratung.',
      market: 'EDEKA München-Schwabing',
      date: '2024-01-27',
      rating: 5
    }, {
      text: 'Frische Backwaren jeden Tag, super Qualität.',
      market: 'EDEKA Frankfurt-Nordend',
      date: '2024-01-26',
      rating: 5
    }, {
      text: 'Die Fischtheke ist hervorragend bestückt.',
      market: 'EDEKA Kiel',
      date: '2024-01-25',
      rating: 5
    }, {
      text: 'Regionale Produkte in bester Qualität.',
      market: 'EDEKA Freiburg',
      date: '2024-01-24',
      rating: 4
    }]
  }, {
    name: 'Freundlichkeit',
    size: 3100,
    sentiment: 'positive',
    comments: [{
      text: 'Personal ist immer freundlich und hilfsbereit.',
      market: 'EDEKA Dresden-Altstadt',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Tolle Beratung an der Käsetheke!',
      market: 'EDEKA Stuttgart-West',
      date: '2024-01-27',
      rating: 5
    }, {
      text: 'Sehr nettes Team, man fühlt sich willkommen.',
      market: 'EDEKA Hannover-List',
      date: '2024-01-26',
      rating: 4
    }]
  }, {
    name: 'Wartezeiten',
    size: 2800,
    sentiment: 'negative',
    comments: [{
      text: 'Zu wenig Kassen offen, lange Wartezeiten.',
      market: 'EDEKA Berlin-Kreuzberg',
      date: '2024-01-28',
      rating: 2
    }, {
      text: 'Samstags ist es unmöglich hier einzukaufen.',
      market: 'EDEKA Köln-Ehrenfeld',
      date: '2024-01-27',
      rating: 1
    }, {
      text: 'Bitte mehr Personal an den Kassen!',
      market: 'EDEKA Dortmund',
      date: '2024-01-26',
      rating: 2
    }, {
      text: 'Wartezeit von 15 Minuten ist nicht akzeptabel.',
      market: 'EDEKA Duisburg',
      date: '2024-01-25',
      rating: 2
    }]
  }, {
    name: 'Sortiment',
    size: 2400,
    sentiment: 'positive',
    comments: [{
      text: 'Riesige Auswahl, findet man alles was man braucht.',
      market: 'EDEKA Center München',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Tolles Bio-Sortiment!',
      market: 'EDEKA Frankfurt-Sachsenhausen',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Preise',
    size: 2100,
    sentiment: 'neutral',
    comments: [{
      text: 'Preise sind okay, nicht günstig aber fair.',
      market: 'EDEKA Leipzig-Zentrum',
      date: '2024-01-28',
      rating: 3
    }, {
      text: 'Für die Qualität angemessene Preise.',
      market: 'EDEKA Nürnberg',
      date: '2024-01-27',
      rating: 4
    }, {
      text: 'Etwas teurer als Discounter, aber bessere Qualität.',
      market: 'EDEKA Bremen',
      date: '2024-01-26',
      rating: 3
    }]
  }, {
    name: 'Sauberkeit',
    size: 1800,
    sentiment: 'positive',
    comments: [{
      text: 'Immer sauber und ordentlich.',
      market: 'EDEKA Karlsruhe',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Gepflegter Markt, macht Spaß hier einzukaufen.',
      market: 'EDEKA Düsseldorf-Bilk',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Parkplatz',
    size: 1500,
    sentiment: 'negative',
    comments: [{
      text: 'Parkplatzsituation ist katastrophal.',
      market: 'EDEKA Berlin-Mitte',
      date: '2024-01-28',
      rating: 2
    }, {
      text: 'Zu wenig Parkplätze, besonders abends.',
      market: 'EDEKA Hamburg-Altona',
      date: '2024-01-27',
      rating: 3
    }]
  }, {
    name: 'Öffnungszeiten',
    size: 1200,
    sentiment: 'neutral',
    comments: [{
      text: 'Schade dass sonntags geschlossen ist.',
      market: 'EDEKA Essen',
      date: '2024-01-28',
      rating: 3
    }, {
      text: 'Öffnungszeiten könnten länger sein.',
      market: 'EDEKA Bonn',
      date: '2024-01-27',
      rating: 3
    }]
  }],
  nord: [{
    name: 'Frische & Qualität',
    size: 620,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Freundlichkeit',
    size: 480,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Fischtheke',
    size: 380,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Wartezeiten',
    size: 280,
    sentiment: 'negative',
    comments: []
  }, {
    name: 'Sortiment',
    size: 350,
    sentiment: 'positive',
    comments: []
  }]
  // Add more regions as needed...
};
// 12-month trend data
const monthlyTrendData: Record<string, Array<{
  month: string;
  rating: number;
  reviews: number;
  positivePercent: number;
  neutralPercent: number;
  negativePercent: number;
}>> = {
  konzern: [{
    month: 'Feb 23',
    rating: 4.05,
    reviews: 14200,
    positivePercent: 62,
    neutralPercent: 25,
    negativePercent: 13
  }, {
    month: 'Mär 23',
    rating: 4.08,
    reviews: 15100,
    positivePercent: 63,
    neutralPercent: 24,
    negativePercent: 13
  }, {
    month: 'Apr 23',
    rating: 4.06,
    reviews: 14800,
    positivePercent: 62,
    neutralPercent: 25,
    negativePercent: 13
  }, {
    month: 'Mai 23',
    rating: 4.1,
    reviews: 15500,
    positivePercent: 64,
    neutralPercent: 24,
    negativePercent: 12
  }, {
    month: 'Jun 23',
    rating: 4.12,
    reviews: 16200,
    positivePercent: 65,
    neutralPercent: 23,
    negativePercent: 12
  }, {
    month: 'Jul 23',
    rating: 4.09,
    reviews: 15800,
    positivePercent: 64,
    neutralPercent: 24,
    negativePercent: 12
  }, {
    month: 'Aug 23',
    rating: 4.11,
    reviews: 16000,
    positivePercent: 65,
    neutralPercent: 23,
    negativePercent: 12
  }, {
    month: 'Sep 23',
    rating: 4.14,
    reviews: 16500,
    positivePercent: 66,
    neutralPercent: 23,
    negativePercent: 11
  }, {
    month: 'Okt 23',
    rating: 4.13,
    reviews: 17200,
    positivePercent: 66,
    neutralPercent: 23,
    negativePercent: 11
  }, {
    month: 'Nov 23',
    rating: 4.16,
    reviews: 17800,
    positivePercent: 67,
    neutralPercent: 23,
    negativePercent: 10
  }, {
    month: 'Dez 23',
    rating: 4.18,
    reviews: 18200,
    positivePercent: 67,
    neutralPercent: 23,
    negativePercent: 10
  }, {
    month: 'Jan 24',
    rating: 4.2,
    reviews: 18500,
    positivePercent: 68,
    neutralPercent: 22,
    negativePercent: 10
  }],
  nord: [{
    month: 'Feb 23',
    rating: 4.2,
    reviews: 2100,
    positivePercent: 66,
    neutralPercent: 24,
    negativePercent: 10
  }, {
    month: 'Mär 23',
    rating: 4.22,
    reviews: 2200,
    positivePercent: 67,
    neutralPercent: 23,
    negativePercent: 10
  }, {
    month: 'Apr 23',
    rating: 4.25,
    reviews: 2150,
    positivePercent: 68,
    neutralPercent: 23,
    negativePercent: 9
  }, {
    month: 'Mai 23',
    rating: 4.28,
    reviews: 2300,
    positivePercent: 69,
    neutralPercent: 22,
    negativePercent: 9
  }, {
    month: 'Jun 23',
    rating: 4.3,
    reviews: 2400,
    positivePercent: 70,
    neutralPercent: 22,
    negativePercent: 8
  }, {
    month: 'Jul 23',
    rating: 4.32,
    reviews: 2350,
    positivePercent: 70,
    neutralPercent: 22,
    negativePercent: 8
  }, {
    month: 'Aug 23',
    rating: 4.35,
    reviews: 2450,
    positivePercent: 71,
    neutralPercent: 21,
    negativePercent: 8
  }, {
    month: 'Sep 23',
    rating: 4.33,
    reviews: 2500,
    positivePercent: 70,
    neutralPercent: 22,
    negativePercent: 8
  }, {
    month: 'Okt 23',
    rating: 4.36,
    reviews: 2550,
    positivePercent: 71,
    neutralPercent: 21,
    negativePercent: 8
  }, {
    month: 'Nov 23',
    rating: 4.38,
    reviews: 2580,
    positivePercent: 72,
    neutralPercent: 21,
    negativePercent: 7
  }, {
    month: 'Dez 23',
    rating: 4.4,
    reviews: 2600,
    positivePercent: 72,
    neutralPercent: 21,
    negativePercent: 7
  }, {
    month: 'Jan 24',
    rating: 4.42,
    reviews: 2610,
    positivePercent: 73,
    neutralPercent: 20,
    negativePercent: 7
  }]
};
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string, isRating: boolean = false): number {
  if (period === 'gesamt') return value;
  if (isRating) {
    const noise = period === '30' ? 0.1 : period === '60' ? -0.1 : period === '120' ? 0.05 : -0.05;
    return Math.max(1, Math.min(5, value + noise));
  }
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
// Simple custom treemap component that doesn't rely on Recharts content prop
const SimpleTreemap = ({
  data,
  onClick
}: {
  data: Array<{
    name: string;
    size: number;
    sentiment: string;
  }>;
  onClick: (item: any) => void;
}) => {
  // Calculate total for percentages
  const total = data.reduce((sum, item) => sum + item.size, 0);
  // Sort by size descending
  const sortedData = [...data].sort((a, b) => b.size - a.size);
  // Color mapping
  const getColors = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: '#d4edda',
          border: '#639754',
          text: '#1a3d1a'
        };
      case 'negative':
        return {
          bg: '#f8d7da',
          border: '#E03C32',
          text: '#3d0a0a'
        };
      default:
        return {
          bg: '#fff3cd',
          border: '#FFD301',
          text: '#3d3200'
        };
    }
  };
  return <div className="grid grid-cols-4 gap-2 h-full" data-id="element-512">
      {sortedData.map((item, index) => {
      const colors = getColors(item.sentiment);
      const percentage = (item.size / total * 100).toFixed(0);
      // Determine grid span based on size
      let colSpan = 1;
      let rowSpan = 1;
      if (index === 0) {
        colSpan = 2;
        rowSpan = 2;
      } else if (index === 1 || index === 2) {
        colSpan = 2;
        rowSpan = 1;
      }
      return <div key={item.name} onClick={() => onClick(item)} className="rounded-lg p-3 flex flex-col justify-center items-center text-center cursor-pointer transition-opacity hover:opacity-80" style={{
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`
      }} data-id="element-513">
            <span className="font-semibold text-sm leading-tight" style={{
          color: colors.text
        }} data-id="element-514">
              {item.name}
            </span>
            <span className="text-xs mt-1 opacity-70" style={{
          color: colors.text
        }} data-id="element-515">
              {item.size.toLocaleString('de-DE')}
            </span>
          </div>;
    })}
    </div>;
};
export function KonzernStimmungPage() {
  const [selectedRegion, setSelectedRegion] = useState('konzern');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const [selectedTopic, setSelectedTopic] = useState<(typeof topicClusters)['konzern'][0] | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const rawData = regionData[selectedRegion] || regionData.konzern;
  const rawTopics = topicClusters[selectedRegion] || topicClusters.konzern;
  const rawTrend = monthlyTrendData[selectedRegion] || monthlyTrendData.konzern;
  const currentData = {
    ...rawData,
    positive: applyTimeFactor(rawData.positive, selectedPeriod),
    neutral: applyTimeFactor(rawData.neutral, selectedPeriod),
    negative: applyTimeFactor(rawData.negative, selectedPeriod),
    total: applyTimeFactor(rawData.total, selectedPeriod),
    avgRating: applyTimeFactor(rawData.avgRating, selectedPeriod, true)
  };
  const currentTopics = rawTopics.map(t => ({
    ...t,
    size: applyTimeFactor(t.size, selectedPeriod)
  }));
  // For trend, just slice the array based on period to show fewer months if short period
  let currentTrend = [...rawTrend];
  if (selectedPeriod === '30') currentTrend = rawTrend.slice(-1);else if (selectedPeriod === '60') currentTrend = rawTrend.slice(-2);else if (selectedPeriod === '120') currentTrend = rawTrend.slice(-4);
  const totalSentiments = currentData.positive + currentData.neutral + currentData.negative;
  const positivePercent = Math.round(currentData.positive / totalSentiments * 100);
  const neutralPercent = Math.round(currentData.neutral / totalSentiments * 100);
  const negativePercent = Math.round(currentData.negative / totalSentiments * 100);
  const handleTopicClick = (topic: any) => {
    const fullTopic = currentTopics.find(t => t.name === topic.name);
    if (fullTopic && fullTopic.comments.length > 0) {
      setSelectedTopic(fullTopic);
      setIsDialogOpen(true);
    }
  };
  const treemapData = currentTopics.map(topic => ({
    name: topic.name,
    size: topic.size,
    sentiment: topic.sentiment
  }));
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-516">
      {/* Region Filter */}
      <Card className="!p-4" data-id="element-517">
        <div className="flex flex-col gap-4" data-id="element-518">
          <div className="flex items-center gap-4 flex-wrap" data-id="element-519">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-520">
              <Filter size={16} data-id="element-521" />
              <span data-id="element-522">Region:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-523">
              {regions.map(region => {
              const isSelected = selectedRegion === region.id;
              const Icon = region.icon;
              return <button key={region.id} onClick={() => setSelectedRegion(region.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-edeka-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-524">
                    {Icon && <Icon size={14} data-id="element-525" />}
                    {region.name}
                  </button>;
            })}
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap" data-id="element-526">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-527">
              <Calendar size={16} data-id="element-528" />
              <span data-id="element-529">Zeitraum:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-530">
              {timePeriods.map(period => {
              const isSelected = selectedPeriod === period.id;
              return <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${isSelected ? 'bg-edeka-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-531">
                    {period.label}
                  </button>;
            })}
            </div>
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-532">
        <KPICard title="Durchschnittliche Bewertung" value={currentData.avgRating.toFixed(1)} icon={<TrendingUp size={18} data-id="element-534" />} data-id="element-533" />
        <KPICard title="Gesamtbewertungen" value={currentData.total.toLocaleString('de-DE')} icon={<MessageSquare size={18} data-id="element-536" />} data-id="element-535" />
        <KPICard title="Positive Resonanz" value={`${positivePercent}%`} icon={<ThumbsUp size={18} data-id="element-538" />} data-id="element-537" />
        <KPICard title="Negative Resonanz" value={`${negativePercent}%`} icon={<ThumbsDown size={18} data-id="element-540" />} data-id="element-539" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-541">
        {/* Absolute Resonanz */}
        <Card data-id="element-542">
          <div className="p-6" data-id="element-543">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-544">
              Absolute Resonanz
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-545">
              Verteilung der Sentiment-Analyse
            </p>

            {/* Stacked Bar */}
            <div className="mb-6" data-id="element-546">
              <div className="h-12 rounded-lg overflow-hidden flex" data-id="element-547">
                <div style={{
                width: `${positivePercent}%`,
                backgroundColor: '#639754'
              }} className="flex items-center justify-center" data-id="element-548">
                  {positivePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-549">
                      {positivePercent}%
                    </span>}
                </div>
                <div style={{
                width: `${neutralPercent}%`,
                backgroundColor: '#FFD301'
              }} className="flex items-center justify-center" data-id="element-550">
                  {neutralPercent > 10 && <span className="text-gray-800 text-sm font-semibold" data-id="element-551">
                      {neutralPercent}%
                    </span>}
                </div>
                <div style={{
                width: `${negativePercent}%`,
                backgroundColor: '#E03C32'
              }} className="flex items-center justify-center" data-id="element-552">
                  {negativePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-553">
                      {negativePercent}%
                    </span>}
                </div>
              </div>
            </div>

            {/* Legend with numbers */}
            <div className="space-y-3" data-id="element-554">
              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-555">
                <div className="flex items-center gap-3" data-id="element-556">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#639754'
                }} data-id="element-557">
                    <ThumbsUp size={18} className="text-white" data-id="element-558" />
                  </div>
                  <div data-id="element-559">
                    <div className="font-semibold text-gray-900" data-id="element-560">Positiv</div>
                    <div className="text-xs text-gray-500" data-id="element-561">
                      Zufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-562">
                  <div className="text-2xl font-bold" style={{
                  color: '#639754'
                }} data-id="element-563">
                    {currentData.positive.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-564">
                    {positivePercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fff9db'
            }} data-id="element-565">
                <div className="flex items-center gap-3" data-id="element-566">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#FFD301'
                }} data-id="element-567">
                    <Minus size={18} className="text-gray-800" data-id="element-568" />
                  </div>
                  <div data-id="element-569">
                    <div className="font-semibold text-gray-900" data-id="element-570">Neutral</div>
                    <div className="text-xs text-gray-500" data-id="element-571">
                      Gemischte Meinungen
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-572">
                  <div className="text-2xl font-bold" style={{
                  color: '#b39500'
                }} data-id="element-573">
                    {currentData.neutral.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-574">
                    {neutralPercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-575">
                <div className="flex items-center gap-3" data-id="element-576">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-577">
                    <ThumbsDown size={18} className="text-white" data-id="element-578" />
                  </div>
                  <div data-id="element-579">
                    <div className="font-semibold text-gray-900" data-id="element-580">Negativ</div>
                    <div className="text-xs text-gray-500" data-id="element-581">
                      Unzufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-582">
                  <div className="text-2xl font-bold" style={{
                  color: '#E03C32'
                }} data-id="element-583">
                    {currentData.negative.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-584">
                    {negativePercent}% aller Bewertungen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Themen Cluster Treemap */}
        <Card data-id="element-585">
          <div className="p-6" data-id="element-586">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-587">
              Themen-Cluster
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-588">
              Häufig erwähnte Themen in Bewertungen (klicken für Details)
            </p>

            <div className="h-80" data-id="element-589">
              <SimpleTreemap data={treemapData} onClick={handleTopicClick} data-id="element-590" />
            </div>

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100" data-id="element-591">
              <div className="flex items-center gap-2" data-id="element-592">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#639754'
              }} data-id="element-593"></div>
                <span className="text-xs text-gray-600" data-id="element-594">Positiv</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-595">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#FFD301'
              }} data-id="element-596"></div>
                <span className="text-xs text-gray-600" data-id="element-597">Neutral</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-598">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-599"></div>
                <span className="text-xs text-gray-600" data-id="element-600">Negativ</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 12-Month Trend Chart */}
      <Card data-id="element-601">
        <div className="p-6" data-id="element-602">
          <div className="flex items-center justify-between mb-6" data-id="element-603">
            <div data-id="element-604">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-605">
                Reputationsentwicklung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-606">
                Sentiment-Verteilung der letzten 12 Monate (in %)
              </p>
            </div>
            <div className="flex items-center gap-5 text-sm" data-id="element-607">
              <div className="flex items-center gap-2" data-id="element-608">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#639754'
              }} data-id="element-609"></div>
                <span className="text-gray-600" data-id="element-610">Positiv</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-611">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#FFD301'
              }} data-id="element-612"></div>
                <span className="text-gray-600" data-id="element-613">Neutral</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-614">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-615"></div>
                <span className="text-gray-600" data-id="element-616">Negativ</span>
              </div>
            </div>
          </div>

          <div className="h-72" data-id="element-617">
            <ResponsiveContainer width="100%" height="100%" data-id="element-618">
              <LineChart data={currentTrend} data-id="element-619">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-620" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-621" />
                <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={value => `${value}%`} data-id="element-622" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => {
                const labels: Record<string, string> = {
                  positivePercent: 'Positiv',
                  neutralPercent: 'Neutral',
                  negativePercent: 'Negativ'
                };
                return [`${value}%`, labels[name] || name];
              }} data-id="element-623" />
                <Line type="monotone" dataKey="positivePercent" stroke="#639754" strokeWidth={3} dot={{
                fill: '#639754',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#639754',
                stroke: '#fff',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-624" />
                <Line type="monotone" dataKey="neutralPercent" stroke="#FFD301" strokeWidth={3} dot={{
                fill: '#FFD301',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#FFD301',
                stroke: '#fff',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-625" />
                <Line type="monotone" dataKey="negativePercent" stroke="#E03C32" strokeWidth={3} dot={{
                fill: '#E03C32',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#E03C32',
                stroke: '#fff',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-626" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Trend Summary */}
          {currentTrend.length > 1 && <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-4" data-id="element-627">
              <div className="flex items-center gap-6" data-id="element-628">
                <div className="flex items-center gap-2" data-id="element-629">
                  <TrendingUp size={16} style={{
                color: '#639754'
              }} data-id="element-630" />
                  <span className="text-sm text-gray-600" data-id="element-631">
                    Positiv:{' '}
                    <span className="font-semibold" style={{
                  color: '#639754'
                }} data-id="element-632">
                      {currentTrend[currentTrend.length - 1].positivePercent}%
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-633">
                  <Minus size={16} style={{
                color: '#b39500'
              }} data-id="element-634" />
                  <span className="text-sm text-gray-600" data-id="element-635">
                    Neutral:{' '}
                    <span className="font-semibold" style={{
                  color: '#b39500'
                }} data-id="element-636">
                      {currentTrend[currentTrend.length - 1].neutralPercent}%
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-637">
                  <TrendingDown size={16} style={{
                color: '#E03C32'
              }} data-id="element-638" />
                  <span className="text-sm text-gray-600" data-id="element-639">
                    Negativ:{' '}
                    <span className="font-semibold" style={{
                  color: '#E03C32'
                }} data-id="element-640">
                      {currentTrend[currentTrend.length - 1].negativePercent}%
                    </span>
                  </span>
                </div>
              </div>
              <div className="text-sm text-gray-500" data-id="element-641">
                Veränderung Positiv:{' '}
                <span className="font-semibold" style={{
              color: '#639754'
            }} data-id="element-642">
                  +
                  {currentTrend[currentTrend.length - 1].positivePercent - currentTrend[0].positivePercent}{' '}
                  Pp.
                </span>
              </div>
            </div>}
        </div>
      </Card>

      {/* Topic Detail Dialog */}
      <AnimatePresence data-id="element-643">
        {isDialogOpen && selectedTopic && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-[600] p-4" onClick={() => setIsDialogOpen(false)} data-id="element-644">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-645">
              {/* Dialog Header */}
              <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-646">
                <div className="flex items-center justify-between" data-id="element-647">
                  <div data-id="element-648">
                    <div className="flex items-center gap-3" data-id="element-649">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-650">
                        {selectedTopic.name}
                      </h3>
                      <Badge variant={selectedTopic.sentiment === 'positive' ? 'success' : selectedTopic.sentiment === 'negative' ? 'danger' : 'warning'} data-id="element-651">
                        {selectedTopic.sentiment === 'positive' ? 'Positiv' : selectedTopic.sentiment === 'negative' ? 'Negativ' : 'Neutral'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-652">
                      {selectedTopic.size.toLocaleString('de-DE')} Erwähnungen
                      in Kundenbewertungen
                    </p>
                  </div>
                  <button onClick={() => setIsDialogOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors" data-id="element-653">
                    <X size={20} data-id="element-654" />
                  </button>
                </div>
              </div>

              {/* Dialog Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]" data-id="element-655">
                <div className="space-y-4" data-id="element-656">
                  {selectedTopic.comments.map((comment, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-657">
                      <div className="flex items-start justify-between gap-4 mb-2" data-id="element-658">
                        <div className="flex items-center gap-2" data-id="element-659">
                          <MapPin size={14} className="text-edeka-blue" data-id="element-660" />
                          <span className="text-sm font-medium text-gray-900" data-id="element-661">
                            {comment.market}
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-662">
                          <div className="flex" data-id="element-663">
                            {[...Array(5)].map((_, i) => <span key={i} className={`text-sm ${i < comment.rating ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-664">
                                ★
                              </span>)}
                          </div>
                          <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-665">
                            <Calendar size={12} data-id="element-666" />
                            {new Date(comment.date).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 italic" data-id="element-667">
                        "{comment.text}"
                      </p>
                    </motion.div>)}
                </div>
              </div>

              {/* Dialog Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end" data-id="element-668">
                <Button variant="primary" onClick={() => setIsDialogOpen(false)} data-id="element-669">
                  Schließen
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}